/* eslint-disable prefer-template */
import { reloadTableContext } from 'context/ReloadTableContext'
import React, { useContext, useEffect, useState } from 'react'
import {
  useTable, usePagination, useFilters, useSortBy,
} from 'react-table'
import { translate } from 'utils/propKeysTranslations'
import useQuery from './useQuery'

const useReactTable = ({
  tableData,
  tableDataFetcher,
  paginated,
  columnsToSkip,
  columnsToInclude,
  columnsToReplace,
  aditionalColumns = [],
  columns,
  initialPageSize = 10,
  endpoint = '',
  dataAccessor = '',
  countAccessor = 'count',
  searchOptions,
  orderBy,
  showDeleted = false,
  urlQueries = {},
  loaderWidth,
}) => {
  const { reloadTrigger, clearTrigger } = useContext(reloadTableContext)
  const [totalPages, setTotalPages] = useState(-1)
  const [fullEndpoint, setFullEndpoint] = useState(
    endpoint
      + '?'
      + (paginated ? 'limit=' + initialPageSize + '&offset=0' : '')
      // + (searchOptions ? `&search=${searchOptions}` : '')
      // + (orderBy ? `&orderBy=${orderBy}` : '')

      + Object.entries(urlQueries)
        .map(([key, value]) => `&${key}=${encodeURIComponent(value)}`)
        .join(''),
  )

  const {
    data, setData, makeRequest, fallbackComponent,
  } = useQuery({
    defaultEndpoint: fullEndpoint,
    isControlled: tableData !== undefined,
    loaderHeight: '50vh',
    loaderWidth,
    hideGlobalLoading: true,
  })

  const propsForColumns = tableData?.[0] || data?.[dataAccessor]?.[0] || data?.[0] || []
  // { count: X, [accessor]: data }
  const tableColumns = React.useMemo(
    () =>
      columns || [
        ...buildColumns(
          propsForColumns,
          showDeleted,
          columnsToInclude,
          columnsToSkip,
          columnsToReplace,
        ),
        ...aditionalColumns,
      ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, tableData],
  )

  const memoizedData = React.useMemo(
    () => tableData || data?.[dataAccessor] || data || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, tableData],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    prepareRow,
    state: {
      pageIndex, pageSize, sortBy, filters,
    },
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    gotoPage,
    pageCount,
    pageOptions,
    setAllFilters,
  } = useTable(
    {
      columns: tableColumns,
      data: memoizedData,
      initialState: { pageSize: initialPageSize },
      defaultColumn: {
        fetchTableData: tableDataFetcher || makeRequest,
      },
      autoResetPage: false,
      // options for controlled pagination
      manualPagination: true,
      pageCount: totalPages,
    },
    useFilters,
    useSortBy,
    usePagination,
  )

  useEffect(() => {
    let queries = '?'
    if (paginated) {
      queries += 'limit=' + pageSize + '&offset=' + pageIndex * pageSize
      if (Object.values(searchOptions).length) {
        queries += `&search=${encodeURIComponent(
          JSON.stringify(searchOptions),
        )}`
      }
      if (Object.values(orderBy).length) {
        queries += `&orderBy=${encodeURIComponent(JSON.stringify(orderBy))}`
      }
    }
    queries += Object.entries(urlQueries)
      .map(([key, value]) => `&${key}=${value}`)
      .join('')
    setFullEndpoint(endpoint + queries)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, pageIndex, pageSize, searchOptions, orderBy, urlQueries])

  useEffect(() => {
    if (data) setTotalPages(Math.ceil(data[countAccessor] / pageSize) || 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (reloadTrigger !== undefined && !!data) {
      makeRequest({ hideGlobalLoading: true })
    }
    return () => clearTrigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger])

  return {
    data: tableData || data,
    setData,
    fallbackComponent,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    count: data?.count ?? data?.length ?? tableData?.length,
    prepareRow,
    pageIndex,
    pageSize,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    gotoPage,
    pageCount,
    pageOptions,
    setAllFilters,
    filters,
  }
}

export default useReactTable

export function formatKey(key) {
  return key
    .slice(key.lastIndexOf('.') + 1)
    .replaceAll(/((^[a-zA-Z])|([A-Z]))/g, (_, __, match1, match2) => {
      return `${match2 ? ' ' : ''}${(match1 || match2).toUpperCase()}`
    })
}

export function flatKeys(obj, superKey, isArray) {
  return Object.entries(obj).reduce((prev, [nextKey, nextValue]) => {
    let wholeKey = nextKey
    if (superKey) {
      wholeKey = isArray ? `${superKey}[${nextKey}]` : `${superKey}.${nextKey}`
    }
    return nextValue instanceof Object
      ? [...prev, ...flatKeys(nextValue, wholeKey, nextValue instanceof Array)]
      : [...prev, wholeKey]
  }, [])
}

export function buildColumns(
  obj,
  showDeleted,
  columnsToInclude,
  columnsToSkip = [],
  columnsToReplace = {},
) {
  if (columnsToInclude) {
    return columnsToInclude.map((c) => ({ Header: translate(c), accessor: c }))
  }
  const values = Object.entries(obj).reduce((prev, [nextKey, nextValue]) => {
    if (
      [...columnsToSkip, 'id']?.includes(nextKey)
      || nextValue?.startsWith?.('/')
      || nextKey.endsWith('Id')
      || nextKey === 'updatedAt'
      || (!showDeleted && nextKey === 'deletedAt')
      || nextKey === 'medium'
    ) {
      return prev
    }
    columnsToReplace[nextKey]
    return [
      ...prev,
      columnsToReplace[nextKey] || {
        Header: translate(nextKey),
        accessor: nextKey,
      },
    ]
  }, [])
  return values
}

export const Filter = ({ filterValue, setFilter }) => {
  // console.log({ props })
  const [value, setValue] = useState(filterValue)
  // const { filterValue, setFilter } = props.column
  useEffect(() => {
    filterValue ?? setValue('')
  }, [filterValue])
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        setFilter(value)
      }}
      className='flex align-center'
    >
      <input
        className='max-w-[100px] border-[2px] border-black rounded'
        value={value || ''}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className='flex items-center relative ml-[4px]'>
        {filterValue && (
          <button
            onClick={() => setFilter('')}
            type='button'
            className='inline-block absolute w-4 leading-4 translate-x-[-1.5em] rounded-full backdrop-blur-[2px] p-[2px] w-[1em] z-[10000] cursor-pointer'
          >
            X
          </button>
        )}
        <button className='hover:text-slate-800' type='submit'>
          Filtrar
        </button>
      </div>
    </form>
  )
}

/* export function parseAccessor(data, accessor) {
  console.log(data)
  if (data.length === 0) return data
  const splittedAccessor = accessor
    .replaceAll(/(\[)(.+)(])/g, (_, __, second, ___) => {
      return `.${second}`
    })
    .split('.')
  return splittedAccessor.reduce((p, c) => p[c], data)
}
 */
